import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiGet, apiPatch, apiDelete, apiPost, ApiClientError } from '@client/lib/api';
import { FieldError } from '@client/components/FieldError';
import { customerLabel } from '@shared/customerLabel';
import { formatPhone } from '@shared/phone';
import { validate, type FieldErrors } from '@client/lib/formErrors';
import { updateCustomerSchema } from '@shared/schemas/customer';
import { createPropertySchema, updatePropertySchema } from '@shared/schemas/property';
import styles from './CustomerDetailPage.module.css';

/** A customer as returned by GET /api/v1/customers/:id. */
interface Customer {
  id: string;
  firstName: string;
  lastName: string;
  companyName: string | null;
  phone: string | null;
  email: string | null;
  isActive: boolean;
}

/** A property as returned by GET /api/v1/properties?customerId=:id. */
interface Property {
  id: string;
  addressLine1: string;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  gateCode: string | null;
  accessNotes: string | null;
  isActive: boolean;
}

/** The server response after PATCHing a customer: the full updated row. */
interface UpdatedCustomer {
  id: string;
  firstName: string;
  lastName: string;
  companyName: string | null;
  phone: string | null;
  email: string | null;
  isActive: boolean;
}

/** The server response after PATCHing a property: the full updated row. */
interface UpdatedProperty {
  id: string;
  addressLine1: string;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  gateCode: string | null;
  accessNotes: string | null;
  isActive: boolean;
}

/** The server response after POSTing a property: the full new row. */
interface CreatedProperty {
  id: string;
  addressLine1: string;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  gateCode: string | null;
  accessNotes: string | null;
  isActive: boolean;
}

/** The server response after DELETEing a customer: the archived row. */
interface DeletedCustomer {
  id: string;
  isActive: boolean;
}

/** Format a property address for display. */
function formatAddress(p: Property): string {
  const parts = [p.addressLine1, p.addressLine2, p.city, p.state, p.postalCode].filter(
    (p): p is string => p != null && p !== ''
  );
  return parts.join(', ');
}

type ViewState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'loaded'; customer: Customer; properties: Property[] };

/**
 * The Customer detail view.
 *
 * GET /api/v1/customers/:id for the customer, and
 * GET /api/v1/properties?customerId=:id for their properties.
 *
 * Editing a field PATCHes it; on success the field re-renders from the
 * server's response. Deleting the customer soft-deletes it (the record stays
 * and its history is preserved — we describe this to the owner as archiving).
 * Adding a property POSTs to /api/v1/properties with this customer's id.
 *
 * No optimistic updates: nothing changes until the server confirms.
 */
export function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [state, setState] = useState<ViewState>({ kind: 'loading' });

  /** id of the customer field currently being saved, or null. */
  const [savingField, setSavingField] = useState<string | null>(null);
  /** error message for a failed customer save, or null. */
  const [saveError, setSaveError] = useState<string | null>(null);
  /** The committed (server-confirmed) value of the customer field currently
   * being edited, captured on focus so we can revert on blur. */
  const [editingFieldOriginal, setEditingFieldOriginal] = useState<string | null>(null);
  /** id of the property currently being saved, or null. */
  const [savingProperty, setSavingProperty] = useState<string | null>(null);
  /** error message for a failed property save, or null. */
  const [propertyError, setPropertyError] = useState<string | null>(null);
  /** Ids currently being archived, for per-row pending state. */
  const [archivingIds, setArchivingIds] = useState<Set<string>>(new Set());
  /**
   * Exactly-once guard, keyed PER PROPERTY ID. A single boolean would freeze
   * every row while one was archiving; two taps on the SAME row must still
   * send one request.
   */
  const archivingRefs = useRef<Record<string, boolean>>({});
  /** The committed value of the property field currently being edited. */
  const [editingPropertyOriginal, setEditingPropertyOriginal] = useState<string | null>(null);
  /** whether the delete is in flight. */
  const [deleting, setDeleting] = useState(false);
  /** error message for a failed delete, or null. */
  const [deleteError, setDeleteError] = useState<string | null>(null);
  /** whether the new-property form is open. */
  const [showPropertyForm, setShowPropertyForm] = useState(false);
  /** draft field values for the new-property form. */
  const [propertyForm, setPropertyForm] = useState({
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    gateCode: '',
    accessNotes: '',
  });
  /** error message for a failed property creation, or null. */
  const [createPropertyError, setCreatePropertyError] = useState<string | null>(null);
  /** per-input validation messages for the new-property form. */
  const [propertyFieldErrors, setPropertyFieldErrors] = useState<FieldErrors>({});
  /** whether the new-property creation is in flight. */
  const [creatingProperty, setCreatingProperty] = useState(false);

  const fetchCustomer = useCallback(async () => {
    if (!id) return;
    setState({ kind: 'loading' });
    try {
      const customer = await apiGet<Customer>(`/api/v1/customers/${id}`);
      const properties = await apiGet<Property[]>(
        `/api/v1/properties?customerId=${encodeURIComponent(id)}`
      );
      setState({ kind: 'loaded', customer, properties });
    } catch (err) {
      setState({
        kind: 'error',
        message: err instanceof ApiClientError ? err.message : 'Could not load this customer.',
      });
    }
  }, [id]);

  useEffect(() => {
    void fetchCustomer();
  }, [fetchCustomer]);

  const handleCustomerFieldChange =
    (field: keyof Customer) => (e: React.ChangeEvent<HTMLInputElement>) => {
      if (state.kind !== 'loaded') return;
      setState({
        kind: 'loaded',
        customer: { ...state.customer, [field]: e.target.value },
        properties: state.properties,
      });
    };

  const handleCustomerFieldBlur = useCallback(
    async (field: keyof Customer) => {
      if (state.kind !== 'loaded') return;
      const newValue = state.customer[field];
      const oldValue = editingFieldOriginal;
      // Only PATCH if the value actually changed.
      if (newValue === oldValue) return;

      // Check the one field being saved against the same schema the server
      // uses, before spending a round trip on a value that cannot be stored.
      // This edits field-at-a-time, so a single-key object is exactly what the
      // PATCH body will be.
      const check = validate(updateCustomerSchema, { [field]: newValue });
      if (!check.ok) {
        setState((prev) => {
          if (prev.kind !== 'loaded') return prev;
          return {
            kind: 'loaded',
            customer: { ...prev.customer, [field]: oldValue },
            properties: prev.properties,
          };
        });
        setSaveError(check.fields[field] ?? check.form ?? 'That value is not valid.');
        return;
      }

      // NO OPTIMISTIC UPDATE: revert the displayed value to the old value
      // immediately, before the request resolves. The input is disabled while
      // the save is in flight. Only the server's response will update the
      // displayed value.
      setState((prev) => {
        if (prev.kind !== 'loaded') return prev;
        return {
          kind: 'loaded',
          customer: { ...prev.customer, [field]: oldValue },
          properties: prev.properties,
        };
      });
      setSavingField(field);
      setSaveError(null);
      try {
        // The PARSED value, so a phone typed as `5045550199` is sent — and
        // stored — as `(504) 555-0199`. Sending the raw text would leave the
        // normalisation to the server alone, which works, but then the screen
        // shows what was typed until the response lands and silently changes
        // under the person who typed it.
        const updated = await apiPatch<UpdatedCustomer>(
          `/api/v1/customers/${state.customer.id}`,
          check.data
        );
        // Re-render from the SERVER's response.
        setState((prev) => {
          if (prev.kind !== 'loaded') return prev;
          return { kind: 'loaded', customer: updated, properties: prev.properties };
        });
      } catch (err) {
        // On failure: the old value is already shown. Surface the server's
        // message.
        setSaveError(
          err instanceof ApiClientError
            ? err.message
            : 'Could not save this change. Please try again.'
        );
      } finally {
        setSavingField(null);
      }
    },
    [state, editingFieldOriginal]
  );

  /**
   * Canonicalise the phone number, then run the ordinary save.
   *
   * Without this the number is normalised anyway — the schema does it — but not
   * until the server has answered, so the value visibly rewrites itself a
   * moment after the person has moved on. Formatting first means what they see
   * on blur is what gets stored.
   */
  const handlePhoneBlur = useCallback(async () => {
    if (state.kind !== 'loaded') return;
    const typed = state.customer.phone ?? '';
    const formatted = formatPhone(typed);
    if (formatted && formatted !== typed) {
      setState((prev) =>
        prev.kind === 'loaded'
          ? { ...prev, customer: { ...prev.customer, phone: formatted } }
          : prev
      );
    }
    await handleCustomerFieldBlur('phone');
  }, [state, handleCustomerFieldBlur]);

  const handleDelete = useCallback(async () => {
    if (state.kind !== 'loaded') return;
    if (
      !window.confirm(
        'Archive this customer? Their record and history will be kept, but they will no longer appear in your active list.'
      )
    ) {
      return;
    }
    setDeleting(true);
    setDeleteError(null);
    try {
      await apiDelete<DeletedCustomer>(`/api/v1/customers/${state.customer.id}`);
      // Navigate back to the list; the customer is now archived.
      navigate('/customers');
    } catch (err) {
      setDeleteError(
        err instanceof ApiClientError
          ? err.message
          : 'Could not archive this customer. Please try again.'
      );
    } finally {
      setDeleting(false);
    }
  }, [state, navigate]);

  const handlePropertyFieldChange =
    (propertyId: string, field: keyof Property) => (e: React.ChangeEvent<HTMLInputElement>) => {
      if (state.kind !== 'loaded') return;
      setState({
        kind: 'loaded',
        customer: state.customer,
        properties: state.properties.map((p) =>
          p.id === propertyId ? { ...p, [field]: e.target.value } : p
        ),
      });
    };

  /**
   * Archive a property.
   *
   * DELETE /api/v1/properties/:id is a SOFT delete: it sets isActive false and
   * returns 200 with the updated row. The property does not vanish, and past
   * jobs, quotes and invoices keep pointing at it — which is why the control
   * says "Archive" and the confirmation says what actually happens. Calling
   * this "Delete" would promise something the server does not do.
   *
   * The list endpoint filters to isActive, so the row is dropped locally
   * rather than refetched.
   */
  const archiveProperty = async (property: Property) => {
    if (archivingRefs.current[property.id]) return;
    archivingRefs.current[property.id] = true;
    setArchivingIds((prev) => new Set(prev).add(property.id));
    setPropertyError(null);
    try {
      await apiDelete(`/api/v1/properties/${property.id}`);
      setState((prev) => {
        if (prev.kind !== 'loaded') return prev;
        return {
          kind: 'loaded',
          customer: prev.customer,
          properties: prev.properties.filter((p) => p.id !== property.id),
        };
      });
    } catch (err) {
      // The server's own message, verbatim. It knows why it refused; we do not.
      setPropertyError(
        err instanceof ApiClientError ? err.message : 'Could not archive that property.'
      );
    } finally {
      setArchivingIds((prev) => {
        const next = new Set(prev);
        next.delete(property.id);
        return next;
      });
      archivingRefs.current[property.id] = false;
    }
  };

  const confirmArchiveProperty = (property: Property) => {
    // The ADDRESS, not "this property". On a customer with four addresses the
    // generic wording is a coin toss.
    if (
      window.confirm(
        `Archive this property?\n\n${formatAddress(property)}\n\n` +
          'It is removed from this list, but past jobs, quotes and invoices keep it.'
      )
    ) {
      void archiveProperty(property);
    }
  };

  const handlePropertyFieldBlur = useCallback(
    async (propertyId: string, field: keyof Property) => {
      if (state.kind !== 'loaded') return;
      const property = state.properties.find((p) => p.id === propertyId);
      if (!property) return;
      const newValue = property[field];
      const oldValue = editingPropertyOriginal;
      if (newValue === oldValue) return;

      // Same check as the customer fields above, against the property schema.
      // Blanking the address here is the case that matters: `addressLine1` is
      // the one field a property cannot lose, because it is where the crew is
      // being sent.
      const check = validate(updatePropertySchema, { [field]: newValue });
      if (!check.ok) {
        setState((prev) => {
          if (prev.kind !== 'loaded') return prev;
          return {
            kind: 'loaded',
            customer: prev.customer,
            properties: prev.properties.map((p) =>
              p.id === propertyId ? { ...p, [field]: oldValue } : p
            ),
          };
        });
        setPropertyError(check.fields[field] ?? check.form ?? 'That value is not valid.');
        return;
      }

      // NO OPTIMISTIC UPDATE: revert the displayed value to the old value
      // immediately, before the request resolves.
      setState((prev) => {
        if (prev.kind !== 'loaded') return prev;
        return {
          kind: 'loaded',
          customer: prev.customer,
          properties: prev.properties.map((p) =>
            p.id === propertyId ? { ...p, [field]: oldValue } : p
          ),
        };
      });
      setSavingProperty(propertyId);
      setPropertyError(null);
      try {
        const updated = await apiPatch<UpdatedProperty>(`/api/v1/properties/${propertyId}`, {
          [field]: newValue,
        });
        // Re-render from the SERVER's response.
        setState((prev) => {
          if (prev.kind !== 'loaded') return prev;
          return {
            kind: 'loaded',
            customer: prev.customer,
            properties: prev.properties.map((p) => (p.id === propertyId ? updated : p)),
          };
        });
      } catch (err) {
        // On failure: the old value is already shown. Surface the server's
        // message.
        setPropertyError(
          err instanceof ApiClientError
            ? err.message
            : 'Could not save this change. Please try again.'
        );
      } finally {
        setSavingProperty(null);
      }
    },
    [state, editingPropertyOriginal]
  );

  const handlePropertyFieldChangeForm =
    (field: keyof typeof propertyForm) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setPropertyForm((prev) => ({ ...prev, [field]: e.target.value }));
      // Clear this field's message the moment it is edited, so a corrected
      // value does not keep looking broken.
      setPropertyFieldErrors((prev) => {
        if (prev[field] === undefined) return prev;
        const next = { ...prev };
        delete next[field];
        return next;
      });
    };

  const handleCreateProperty = useCallback(async () => {
    if (state.kind !== 'loaded') return;
    setCreatePropertyError(null);

    // An address is the one thing a property genuinely cannot do without — it
    // is where the crew is being sent. `createPropertySchema` says so
    // (`addressLine1: nonEmptyString`), and checking here names that field
    // rather than letting the server answer about the form as a whole.
    //
    // `customerId` is OMITTED from the client's check on purpose. It is not on
    // the form — it comes from the URL — so a complaint about it is not
    // something the person filling this in can act on, and "Customer id: Enter
    // a valid identifier" reads as though they typed something wrong. It is a
    // bug if it is ever invalid, and the server still validates it.
    const result = validate(createPropertySchema.omit({ customerId: true }), {
      addressLine1: propertyForm.addressLine1,
      addressLine2: propertyForm.addressLine2,
      city: propertyForm.city,
      state: propertyForm.state,
      postalCode: propertyForm.postalCode,
      gateCode: propertyForm.gateCode,
      accessNotes: propertyForm.accessNotes,
    });
    if (!result.ok) {
      setPropertyFieldErrors(result.fields);
      setCreatePropertyError(result.form);
      return;
    }
    setPropertyFieldErrors({});

    setCreatingProperty(true);
    try {
      // customerId is added back here, from the URL rather than the form — the
      // one field the person filling this in does not get a say in.
      const created = await apiPost<CreatedProperty>('/api/v1/properties', {
        customerId: state.customer.id,
        ...result.data,
      });
      // Re-render from the SERVER's response.
      setState((prev) => {
        if (prev.kind !== 'loaded') return prev;
        return {
          kind: 'loaded',
          customer: prev.customer,
          properties: [...prev.properties, created],
        };
      });
      setShowPropertyForm(false);
      setPropertyForm({
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        postalCode: '',
        gateCode: '',
        accessNotes: '',
      });
    } catch (err) {
      setCreatePropertyError(
        err instanceof ApiClientError
          ? err.message
          : 'Could not create this property. Please try again.'
      );
    } finally {
      setCreatingProperty(false);
    }
  }, [state, propertyForm]);

  const isPropertyFormEmpty =
    !propertyForm.addressLine1 &&
    !propertyForm.addressLine2 &&
    !propertyForm.city &&
    !propertyForm.state &&
    !propertyForm.postalCode &&
    !propertyForm.gateCode &&
    !propertyForm.accessNotes;

  if (state.kind === 'loading') {
    return (
      <main id="main" className={styles.container}>
        <p className={styles.status} aria-live="polite">
          Loading customer…
        </p>
      </main>
    );
  }

  if (state.kind === 'error') {
    return (
      <main id="main" className={styles.container}>
        <p className={styles.status} role="alert">
          {state.message}
        </p>
        <button type="button" className={styles.retryButton} onClick={() => void fetchCustomer()}>
          Retry
        </button>
      </main>
    );
  }

  const { customer, properties } = state;

  return (
    <main id="main" className={styles.container}>
      <header className={styles.header}>
        <h1 className={styles.title}>{customerLabel(customer)}</h1>
        <button type="button" className={styles.backButton} onClick={() => navigate('/customers')}>
          Back
        </button>
      </header>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Customer</h2>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>First name</span>
          <input
            type="text"
            className={styles.input}
            value={customer.firstName ?? ''}
            onChange={handleCustomerFieldChange('firstName')}
            onFocus={() => setEditingFieldOriginal(customer.firstName ?? '')}
            onBlur={() => void handleCustomerFieldBlur('firstName')}
            disabled={savingField === 'firstName'}
            aria-busy={savingField === 'firstName'}
          />
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Last name</span>
          <input
            type="text"
            className={styles.input}
            value={customer.lastName ?? ''}
            onChange={handleCustomerFieldChange('lastName')}
            onFocus={() => setEditingFieldOriginal(customer.lastName ?? '')}
            onBlur={() => void handleCustomerFieldBlur('lastName')}
            disabled={savingField === 'lastName'}
            aria-busy={savingField === 'lastName'}
          />
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Company name</span>
          <input
            type="text"
            className={styles.input}
            value={customer.companyName ?? ''}
            onChange={handleCustomerFieldChange('companyName')}
            onFocus={() => setEditingFieldOriginal(customer.companyName ?? '')}
            onBlur={() => void handleCustomerFieldBlur('companyName')}
            disabled={savingField === 'companyName'}
            aria-busy={savingField === 'companyName'}
          />
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Phone</span>
          <input
            type="tel"
            className={styles.input}
            value={customer.phone ?? ''}
            onChange={handleCustomerFieldChange('phone')}
            onFocus={() => setEditingFieldOriginal(customer.phone ?? '')}
            onBlur={() => void handlePhoneBlur()}
            disabled={savingField === 'phone'}
            aria-busy={savingField === 'phone'}
            aria-label="Phone"
          />
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Email</span>
          <input
            type="email"
            className={styles.input}
            value={customer.email ?? ''}
            onChange={handleCustomerFieldChange('email')}
            onFocus={() => setEditingFieldOriginal(customer.email ?? '')}
            onBlur={() => void handleCustomerFieldBlur('email')}
            disabled={savingField === 'email'}
            aria-busy={savingField === 'email'}
          />
        </div>
        {saveError && (
          <p className={styles.fieldError} role="alert">
            {saveError}
          </p>
        )}
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Properties</h2>
          <button
            type="button"
            className={styles.smallButton}
            onClick={() => setShowPropertyForm((s) => !s)}
          >
            {showPropertyForm ? 'Cancel' : 'Add property'}
          </button>
        </div>

        {showPropertyForm && (
          <div className={styles.propertyForm}>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Address line 1</span>
              <input
                type="text"
                className={styles.input}
                value={propertyForm.addressLine1}
                onChange={handlePropertyFieldChangeForm('addressLine1')}
                aria-label="Address line 1"
                disabled={creatingProperty}
                aria-invalid={propertyFieldErrors.addressLine1 ? 'true' : undefined}
                aria-describedby={
                  propertyFieldErrors.addressLine1 ? 'property-addressLine1-error' : undefined
                }
              />
              <FieldError
                id="property-addressLine1-error"
                message={propertyFieldErrors.addressLine1}
              />
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Address line 2</span>
              <input
                type="text"
                className={styles.input}
                value={propertyForm.addressLine2}
                onChange={handlePropertyFieldChangeForm('addressLine2')}
                aria-label="Address line 2"
                disabled={creatingProperty}
                aria-invalid={propertyFieldErrors.addressLine2 ? 'true' : undefined}
                aria-describedby={
                  propertyFieldErrors.addressLine2 ? 'property-addressLine2-error' : undefined
                }
              />
              <FieldError
                id="property-addressLine2-error"
                message={propertyFieldErrors.addressLine2}
              />
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>City</span>
              <input
                type="text"
                className={styles.input}
                value={propertyForm.city}
                onChange={handlePropertyFieldChangeForm('city')}
                aria-label="City"
                disabled={creatingProperty}
                aria-invalid={propertyFieldErrors.city ? 'true' : undefined}
                aria-describedby={propertyFieldErrors.city ? 'property-city-error' : undefined}
              />
              <FieldError id="property-city-error" message={propertyFieldErrors.city} />
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>State</span>
              <input
                type="text"
                className={styles.input}
                value={propertyForm.state}
                onChange={handlePropertyFieldChangeForm('state')}
                aria-label="State"
                disabled={creatingProperty}
                aria-invalid={propertyFieldErrors.state ? 'true' : undefined}
                aria-describedby={propertyFieldErrors.state ? 'property-state-error' : undefined}
              />
              <FieldError id="property-state-error" message={propertyFieldErrors.state} />
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Postal code</span>
              <input
                type="text"
                className={styles.input}
                value={propertyForm.postalCode}
                onChange={handlePropertyFieldChangeForm('postalCode')}
                aria-label="Postal code"
                disabled={creatingProperty}
                aria-invalid={propertyFieldErrors.postalCode ? 'true' : undefined}
                aria-describedby={
                  propertyFieldErrors.postalCode ? 'property-postalCode-error' : undefined
                }
              />
              <FieldError id="property-postalCode-error" message={propertyFieldErrors.postalCode} />
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Gate code</span>
              <input
                type="text"
                className={styles.input}
                value={propertyForm.gateCode}
                onChange={handlePropertyFieldChangeForm('gateCode')}
                aria-label="Gate code"
                disabled={creatingProperty}
                aria-invalid={propertyFieldErrors.gateCode ? 'true' : undefined}
                aria-describedby={
                  propertyFieldErrors.gateCode ? 'property-gateCode-error' : undefined
                }
              />
              <FieldError id="property-gateCode-error" message={propertyFieldErrors.gateCode} />
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Access notes</span>
              <input
                type="text"
                className={styles.input}
                value={propertyForm.accessNotes}
                onChange={handlePropertyFieldChangeForm('accessNotes')}
                aria-label="Access notes"
                disabled={creatingProperty}
                aria-invalid={propertyFieldErrors.accessNotes ? 'true' : undefined}
                aria-describedby={
                  propertyFieldErrors.accessNotes ? 'property-accessNotes-error' : undefined
                }
              />
              <FieldError
                id="property-accessNotes-error"
                message={propertyFieldErrors.accessNotes}
              />
            </div>
            {createPropertyError && (
              <p className={styles.fieldError} role="alert">
                {createPropertyError}
              </p>
            )}
            <button
              type="button"
              className={styles.saveButton}
              disabled={creatingProperty || isPropertyFormEmpty}
              aria-busy={creatingProperty}
              onClick={() => void handleCreateProperty()}
            >
              {creatingProperty ? 'Saving…' : 'Save'}
            </button>
          </div>
        )}

        {propertyError && (
          <p className={styles.fieldError} role="alert">
            {propertyError}
          </p>
        )}

        {properties.length === 0 ? (
          <p className={styles.empty}>No properties on file.</p>
        ) : (
          <ul className={styles.propertyList}>
            {properties.map((property) => (
              <li key={property.id} className={styles.propertyRow}>
                <div className={styles.propertyInfo}>
                  <span className={styles.propertyAddress}>{formatAddress(property)}</span>
                  <button
                    type="button"
                    className={styles.archiveButton}
                    onClick={() => confirmArchiveProperty(property)}
                    disabled={archivingIds.has(property.id)}
                    aria-busy={archivingIds.has(property.id)}
                    aria-label={`Archive ${formatAddress(property)}`}
                  >
                    Archive
                  </button>
                  {property.gateCode && (
                    <span className={styles.propertyGateCode}>Gate code: {property.gateCode}</span>
                  )}
                  {property.accessNotes && (
                    <span className={styles.propertyAccessNotes}>{property.accessNotes}</span>
                  )}
                </div>
                <div className={styles.propertyFields}>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>Gate code</span>
                    <input
                      type="text"
                      className={styles.input}
                      value={property.gateCode ?? ''}
                      onChange={handlePropertyFieldChange(property.id, 'gateCode')}
                      onFocus={() => setEditingPropertyOriginal(property.gateCode ?? '')}
                      onBlur={() => void handlePropertyFieldBlur(property.id, 'gateCode')}
                      disabled={savingProperty === property.id}
                      aria-busy={savingProperty === property.id}
                    />
                  </div>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>Access notes</span>
                    <input
                      type="text"
                      className={styles.input}
                      value={property.accessNotes ?? ''}
                      onChange={handlePropertyFieldChange(property.id, 'accessNotes')}
                      onFocus={() => setEditingPropertyOriginal(property.accessNotes ?? '')}
                      onBlur={() => void handlePropertyFieldBlur(property.id, 'accessNotes')}
                      disabled={savingProperty === property.id}
                      aria-busy={savingProperty === property.id}
                    />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Archive customer</h2>
        <p className={styles.archiveNote}>
          Archiving removes this customer from your active list. Their record and job history are
          kept.
        </p>
        {deleteError && (
          <p className={styles.fieldError} role="alert">
            {deleteError}
          </p>
        )}
        <button
          type="button"
          className={styles.dangerButton}
          disabled={deleting}
          aria-busy={deleting}
          onClick={() => void handleDelete()}
        >
          {deleting ? 'Archiving…' : 'Archive customer'}
        </button>
      </section>
    </main>
  );
}
